import React, {useEffect, useRef, useState} from 'react';
import {useLoaderData, useParams} from "react-router";
import {navigateHook} from "../../../../../hooks/use-navigate.js";
import {useQuery} from "@tanstack/react-query";
import projectUtils from "../../../../../utils/project-utils.js";
import {Template} from "../../../../../components/template/template.jsx";
import Button from "../../../../../components/button/button.jsx";
import {Card} from "../../../../../components/card/card.jsx";
import InputForm from "../../../../../components/input/input-form.jsx";
import solicitationService from "../../../../../services/web/solicitation-service.js";
import InputDatetime from "../../../../../components/input/input-datetime.jsx";
import useFetchData from "./hooks/use-fetch-data.js";
import SelectComponent from "../../../../../components/select/select-component.jsx";
import InputTextarea from "../../../../../components/input/input-textarea.jsx";
import Dropzone from "../../../../../components/dropzone/dropzone.jsx";
import {Modal} from "../../../../../components/modal/modal.jsx";
import {FaCheck, FaFile, FaFileLines, FaXmark} from "react-icons/fa6";
import SentryRow from "../../../../../components/template/sentry-row.jsx";
import I18nKeys from "../components/i18n-keys.js";

const initialState = {
    applicant: "",
    organization: "",
    nature: "",
    description: "",
    incidentDatetime: "",
    userObservation: "",
    groups: []
}

const SolicitationForm = () => {
    const {id} = useParams();
    const navigate = navigateHook();

    const loaderData = useLoaderData();

    const [isLoading, setIsLoading] = useState(false);
    const [saveStatus, setSaveStatus] = useState("idle");
    const [deleteStatus, setDeleteStatus] = useState("idle");
    const [state, setState] = useState(initialState)
    const [attaches, setAttaches] = useState([]);
    const saveStatusTimeoutRef = useRef(null);
    const deleteStatusTimeoutRef = useRef(null);

    useEffect(() => () => {
        clearTimeout(saveStatusTimeoutRef.current);
        clearTimeout(deleteStatusTimeoutRef.current);
    }, []);

    const [target, setTarget] = useState("");
    const [templateId, setTemplateId] = useState(null);
    const [showTemplateModal, setShowTemplateModal] = useState(false);

    const fetchData = useFetchData();

    useEffect(() => {
        return () => setState(null);
    }, []);

    const {data, refetch, isError, error} = useQuery({
        queryFn: () => solicitationService.get(id),
        queryKey: ['ecap-solicitation'],
        retry: false,
        enabled: !!id
    });

    useEffect(() => {
        if (isError && error?.response?.data?.message === "not.authorized") {
            navigate("/401");
            return;
        }

        if (!!id && data && fetchData.occurrenceGroup.isSuccess) {
            const solicitation = data.solicitation;

            setState({
                applicant: solicitation.applicant,
                organization: solicitation.organization,
                nature: solicitation.natureId,
                description: solicitation.description,
                incidentDatetime: solicitation.incidentDatetime,
                userObservation: solicitation.userObservation ?? "",
                groups: data.groups?.map(g => ({...g, value: parseInt(g.value)}))
            });

            setAttaches(solicitation.attaches);
        } else  {
            setState(initialState);
            setAttaches([]);
        }
    }, [id, data, fetchData.occurrenceGroup.isSuccess, isError]);

    const submit = (finalized = false, addAfter = false, showSaveFeedback = false) => {
        if (showSaveFeedback) {
            clearTimeout(saveStatusTimeoutRef.current);
            setSaveStatus("loading");
        } else {
            setIsLoading(true);
        }

        const formData = new FormData();

        formData.append("description", state.description);
        formData.append("applicant", state.applicant);
        if (state.organization) formData.append("organization", state.organization);
        if (state.nature) formData.append("natureId", state.nature);
        formData.append("incidentDatetime", state.incidentDatetime instanceof Date ? state.incidentDatetime.toISOString() : state.incidentDatetime);
        formData.append("userObservation", state.userObservation);
        formData.append("attaches", JSON.stringify(attaches.filter(file => !file?.new)));
        formData.append("finalized", finalized);
        formData.append("groups", state.groups?.map(g => g.value));

        attaches.filter(file => file.new).forEach(file => formData.append("files", file));

        let response;
        if (id) response = solicitationService.edit(id, formData);
        else response = solicitationService.create(formData);

        response
            .then((data) => {
                if (showSaveFeedback) {
                    projectUtils.success(false, "Salvo com sucesso");
                    setSaveStatus("success");
                    saveStatusTimeoutRef.current = setTimeout(() => setSaveStatus("idle"), 1500);
                } else {
                    projectUtils.success(!id || addAfter);
                }

                if (id && !addAfter) {
                    refetch();
                    return;
                }

                let urlDev = `/ecap-solicitation/form/${data}`;
                if (addAfter) urlDev = `/ecap-solicitation/form`;

                navigate(urlDev);
            })
            .catch((error) => {
                console.error(error);
                if (showSaveFeedback) setSaveStatus("idle");

                let message = 'Não foi possível salvar.';
                if (error.response.status !== 500) message = translateErrMessage(error.response.data, message);

                projectUtils.swalErrorMessage('', message, 'Ok');
            })
            .finally(() => {
                if (!showSaveFeedback) setIsLoading(false);
            });
    }

    const deleteSolicitation = () => {
        projectUtils.swalMessage(
            "warning",
            "Excluir",
            "Excluir Solicitação?",
            "Sim",
            true,
            "Não",
            ({isConfirmed}) => {
                if (isConfirmed) {
                    clearTimeout(deleteStatusTimeoutRef.current);
                    setDeleteStatus("loading");

                    solicitationService.delete(id)
                        .then(() => {
                            setDeleteStatus("success");
                            projectUtils.success(true, "Excluído com sucesso");
                            navigate("/ecap-solicitation");
                        })
                        .catch((error) => {
                            console.error(error);
                            setDeleteStatus("idle");

                            let message = I18nKeys[error.response.data] ? I18nKeys[error.response.data] : 'Não foi possível excluir.';
                            if (error.response.status !== 500) message = translateErrMessage(error.response.data, message);

                            projectUtils.swalErrorMessage('', message, 'Ok')
                        })
                }
            }
        );
    }

    const reopen = () => {
        setIsLoading(true);

        solicitationService.reopen(id)
            .then(() => {
                refetch()
            })
            .catch((error) => {
                console.error(error);
                let message = 'Não foi possível prosseguir.';
                if (error.response.status !== 500) {
                    message = translateErrMessage(error.response.data, message);
                }

                projectUtils.swalErrorMessage('', message, 'Ok')
            })
            .finally(() => setIsLoading(false));
    }

    const translateErrMessage = (message, defaultMessage) => {
        const err = {
            "missing.applicant": "O campo \"Solicitante\" é obrigatório.",
            "missing.incidentDate": "O campo \"Data/hora do fato\" é obrigatório.",
            "missing.nature": "O campo \"Natureza\" é obrigatório.",
            "notValid.incidentDate": "Insira uma data/hora válida.",
            "missing.organization": "O campo \"Organização\" é obrigatório.",
        }

        return err[message] ?? defaultMessage
    }

    const createNewEcap = (showModal = true) => {
        if (showModal && fetchData.templates.length) {
            setShowTemplateModal(true);
            return;
        }

        const params = new URLSearchParams({solicitationId: id});
        if (templateId && templateId.value !== "-1") params.append("templateId", templateId.value.toString());

        navigate(`/ecap/form?${params}`, true);
    }

    const accessEcap = () => {
        const ecap = data?.existsEcap?.replace("/", "-");
        navigate(`/ecap/form/${ecap}`, true);
    }

    const blankFile = () => {
        setTarget("blank")
        setTemplateId(null);
    }

    const templateFile = () => setTarget("template");

    const fileWithTemplate = (value) => setTemplateId(value);

    return (
        <>
            <Template isLoading={isLoading}>
                <Template.Header>
                    <Template.Title browserTitle={'Solicitação'}>
                        Solicitação {id && id + " (" + data?.statusLang + ")"}
                    </Template.Title>

                    {
                        id && !data?.existsEcap && !data?.solicitation?.isFinalized &&
                        <Button target={"success"} disabled={data?.solicitation?.isFinalized} onClick={() => createNewEcap()}>
                            Criar {loaderData?.ecap_acronym}
                        </Button>
                    }
                    {
                        !!data?.existsEcap &&
                        <Button target={"success"} onClick={accessEcap}>
                            Acessar {loaderData?.ecap_acronym}
                        </Button>
                    }
                </Template.Header>

                <Template.Body className={"space-y-2"}>
                    <Card>
                        <Card.Header>
                            <Card.Title>Informações</Card.Title>
                        </Card.Header>
                        <Card.Body>
                            <SentryRow>
                                <InputDatetime
                                    classNameWrapper={"col-span-2"}
                                    id={"dtStart"}
                                    label={"Data/hora do fato *"}
                                    selected={state.incidentDatetime}
                                    dateFormat="LL"
                                    disabled={data?.isFinalized}
                                    onChange={(date) => setState(prev => ({...prev, incidentDatetime: date }))}
                                />

                                <InputForm
                                    id={"applicant"}
                                    classNameWrapper={"col-span-2"}
                                    label={"Solicitante *"}
                                    value={state.applicant}
                                    onChange={({target: {value}}) => setState(prev => ({...prev, applicant: value}))}
                                    disabled={data?.isFinalized}
                                />

                                <SelectComponent
                                    classNameWrapper={"col-span-2"}
                                    value={state.organization}
                                    options={fetchData.organizations}
                                    label={"Organização *"}
                                    onChange={(value) => setState(prev => ({...prev, organization: value?.value}))}
                                    isDisabled={data?.isFinalized}
                                />

                                <SelectComponent
                                    classNameWrapper={"col-span-3"}
                                    value={state.nature}
                                    options={fetchData.reasons}
                                    label={"Natureza *"}
                                    onChange={(value) => setState(prev => ({...prev, nature: value?.value}))}
                                    isDisabled={data?.isFinalized}
                                />

                                <SelectComponent
                                    classNameWrapper={"col-span-3"}
                                    value={state.groups}
                                    options={fetchData.occurrenceGroup.data ?? []}
                                    label={"Grupo de operação"}
                                    onChange={(value) => setState(prev => ({...prev, groups: value}))}
                                    isMulti
                                />

                                <InputTextarea
                                    value={state.description}
                                    label="Descrição da solicitação"
                                    className="col-span-12"
                                    textAreaClass={"h-[100px]"}
                                    onChange={({target}) => setState(prev => ({...prev, description: target.value}))}
                                    disabled={data?.isFinalized}
                                />

                                <InputTextarea
                                    value={state.userObservation}
                                    label="Observação do agente"
                                    className="col-span-12"
                                    textAreaClass={"h-[100px]"}
                                    onChange={({target}) => setState(prev => ({...prev, userObservation: target.value}))}
                                    disabled={data?.isFinalized}
                                />
                            </SentryRow>
                        </Card.Body>
                    </Card>

                    <Dropzone
                        attaches={attaches}
                        setAttaches={setAttaches}
                        title={"Anexos"}
                        disabled={data?.isFinalized}
                    />

                    <Card>
                        <Card.Body className={"flex gap-2 " + (id ? "justify-between" : "justify-end")}>
                            {
                                id &&
                                <p className={"report-center"}>
                                    Cadastrado por <b>{data?.createdUserName}</b> em <b>{projectUtils.formatDate(data?.solicitation?.createdAt)}</b>
                                </p>
                            }

                            <section className={"flex gap-2"}>
                                {
                                    id && !data?.isFinalized &&
                                    <>
                                        <Button
                                            target={"danger"}
                                            onClick={deleteSolicitation}
                                            actionFeedbackType="delete"
                                            status={deleteStatus}
                                            loadingText="Excluindo..."
                                            loadingClassName="!text-gray-900 !opacity-100"
                                            successText="Excluído com sucesso"
                                            successIcon={<FaXmark size={17}/>}
                                            successClassName="disabled:!bg-green-700 disabled:opacity-100!"
                                        >
                                            Excluir
                                        </Button>

                                        <Button target={"warning"} onClick={() => submit(true)}>
                                            Finalizar
                                        </Button>
                                    </>
                                }

                                {
                                    !data?.isFinalized &&
                                    <>
                                        <Button target={"default"} onClick={() => submit(false, true)} actionFeedbackType="save">
                                            Salvar e adicionar novo
                                        </Button>

                                        <Button
                                            onClick={() => submit(false, false, true)}
                                            actionFeedbackType="save"
                                            status={saveStatus}
                                            loadingText="Salvando..."
                                            loadingClassName="!text-gray-900 !opacity-100"
                                            successText="Salvo com sucesso"
                                            successIcon={<FaCheck/>}
                                            successClassName="!bg-green-700 !text-white !opacity-100"
                                        >
                                            Salvar
                                        </Button>
                                    </>
                                }

                                {
                                    data?.isFinalized &&
                                    <>
                                        <Button target={"warning"} onClick={reopen}>
                                            Reabrir
                                        </Button>
                                    </>
                                }
                            </section>
                        </Card.Body>
                    </Card>
                </Template.Body>
            </Template>

            <Modal title={<span className={"text-center m-0! w-full"}>Criar {loaderData?.ecap_acronym}</span>} open={showTemplateModal} setOpen={setShowTemplateModal} className={"w-xl"} overflow={true}>
                <Modal.Body className={"px-5 py-10 mt-0! flex justify-between items-start gap-5"}>
                    <section className={"text-center cursor-pointer hover:text-(--blue-dark) w-[45%] flex flex-col items-center" + (target === "blank" ? " text-(--blue-dark)" : "")} onClick={blankFile}>
                        <FaFile size={90}/>
                        <h2 className={"mt-2"}>Em Branco</h2>
                    </section>

                    <div className={"border border-gray-400 h-40"}></div>

                    <section className={"cursor-pointer hover:text-(--blue-dark) w-[45%] flex flex-col items-center" + (target === "template" ? " text-(--blue-dark)" : "")} onClick={templateFile}>
                        <FaFileLines size={90}/>
                        <h2 className={"my-2"}>A partir de um modelo</h2>
                        <SelectComponent
                            classNameWrapper={"w-full" + (target === "template" ? "" : " invisible")}
                            value={templateId}
                            options={fetchData.templates}
                            label={""}
                            onChange={fileWithTemplate}
                            isDisabled={target !== "template"}
                        />
                    </section>
                </Modal.Body>

                <Modal.Footer>
                    <Button onClick={() => createNewEcap(false)}>
                        Criar
                    </Button>
                </Modal.Footer>
            </Modal>
        </>

    );
};

export default SolicitationForm;